import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, Stepper, money, fmtDate, fmtDateTime, carrierLabel, CARRIER_OPTIONS, DOC_TYPES, downloadStoredFile, CarrierBadge } from "@/components/common";
import { BankAccountPicker } from "@/components/BankAccountsCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FedexAwbDialog } from "@/components/FedexAwbDialog";
import { TrackingTimeline } from "@/components/TrackingTimeline";
import { AiEmailDraft } from "@/components/AiEmailDraft";
import { TransitAttachments } from "@/components/TransitAttachments";
import { OrderProgress } from "@/components/ShipmentContentsSection";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle, XCircle, Truck, Receipt, ClipboardText,
  UploadSimple, FileText, DownloadSimple, PaperPlaneTilt, Package, Trash, PencilSimple, Plus, ArrowsClockwise, AirplaneTilt, ArrowCounterClockwise, Warehouse,
} from "@phosphor-icons/react";

const PROC_STEPS = [
  { key: "not_started", label: "Not started" },
  { key: "sourcing", label: "Sourcing suppliers" },
  { key: "ordered", label: "PO placed with supplier" },
  { key: "received", label: "Goods received" },
];
const SHIP_STEPS = [
  { key: "not_shipped", label: "Not shipped" },
  { key: "preparing", label: "Preparing shipment" },
  { key: "dispatched", label: "Dispatched — handed to courier/airline" },
  { key: "in_transit", label: "In transit" },
  { key: "arrived", label: "Arrived at destination" },
  { key: "delivered", label: "Delivered" },
];

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isTeam, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [order, setOrder] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [poOpen, setPoOpen] = useState(false);
  const [shipOpen, setShipOpen] = useState(false);
  const [fedexOpen, setFedexOpen] = useState(false);
  const [invOpen, setInvOpen] = useState(false);
  const [poSupplier, setPoSupplier] = useState("");
  const [poSplit, setPoSplit] = useState(false);
  const [poItems, setPoItems] = useState([]);
  const [itemCosts, setItemCosts] = useState({});
  const [ship, setShip] = useState({ carrier: "", tracking_number: "", eta: "", origin: "", destination: "", patient_name: "" });
  const [shipLines, setShipLines] = useState([]);
  const [taxRate, setTaxRate] = useState(0);
  const [invCur, setInvCur] = useState("USD");
  const [invBank, setInvBank] = useState("");
  const [invRate, setInvRate] = useState("");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [feeInv, setFeeInv] = useState(null);
  const [feeAmt, setFeeAmt] = useState("");
  const [feeNote, setFeeNote] = useState("");
  const [feeBusy, setFeeBusy] = useState(false);
  const saveTransferFee = async () => {
    setFeeBusy(true);
    try {
      const { data } = await api.patch(`/invoices/${feeInv.id}/transfer-fee`, { transfer_fee: Number(feeAmt) || 0, note: feeNote });
      toast.success(`Invoice ${data.invoice_number} updated · total ${money(data.total)}`);
      setFeeInv(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update invoice"); } finally { setFeeBusy(false); }
  };
  useEffect(() => { if (invOpen && isTeam) api.get("/settings/bank-accounts").then((r) => { setBankAccounts(r.data); const d = r.data.find((a) => a.is_default) || r.data[0]; if (d) { setInvCur(d.currency); setInvBank(d.id); } }).catch(() => {}); }, [invOpen, isTeam]);
  const [shippingCharge, setShippingCharge] = useState(0);
  const [handlingType, setHandlingType] = useState("normal");
  const [coolBoxQty, setCoolBoxQty] = useState(1);
  const [coolBoxPrice, setCoolBoxPrice] = useState(0);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [collectOpen, setCollectOpen] = useState(false);
  const [collect, setCollect] = useState({ collected_by: "", id_reference: "", note: "" });
  const markCollected = async () => {
    if (!collect.collected_by.trim()) return toast.error("Enter who collected the order");
    try {
      await api.post(`/orders/${id}/collected`, collect);
      toast.success("Order marked as collected");
      setCollectOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not mark as collected"); }
  };
  const [dispMode, setDispMode] = useState("courier");
  const [courierName, setCourierName] = useState("");
  const [airlineName, setAirlineName] = useState("");
  const [docBusy, setDocBusy] = useState(null);
  const [editItems, setEditItems] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [ef, setEf] = useState(null);
  const [itemsBusy, setItemsBusy] = useState(false);
  const docRef = useRef();
  const pendingDoc = useRef(null);
  const [transits, setTransits] = useState([]);

  const loadTransits = useCallback(() => { if (isTeam) api.get("/transit-shipments", { params: { order_id: id } }).then((r) => setTransits(r.data || [])).catch(() => {}); }, [id, isTeam]);
  const load = useCallback(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => toast.error("Order not found"));
    loadTransits();
  }, [id, loadTransits]);

  useEffect(() => {
    load();
    if (isTeam) {
      api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
      api.get("/item-costs").then((r) => setItemCosts(r.data || {})).catch(() => {});
    }
  }, [load, isTeam]);

  useEffect(() => {
    if (order) {
      setShippingCharge(order.shipping_charge ?? 0);
      setHandlingType(order.handling_type || "normal");
      setCoolBoxQty(order.cool_box_qty || 1);
      setCoolBoxPrice(order.cool_box_price || 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id]);

  const handlingPayload = () => ({
    handling_type: handlingType,
    cool_box_qty: handlingType === "cool_box" ? Number(coolBoxQty) || 0 : 0,
    cool_box_price: handlingType === "cool_box" ? Number(coolBoxPrice) || 0 : 0,
  });

  const patchStatus = async (payload) => {
    try {
      await api.patch(`/orders/${id}/status`, payload);
      toast.success("Order updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const openPoDialog = () => {
    setPoItems((order.items || []).map((it) => {
      const key = (it.sku || it.name || "").trim().toLowerCase();
      return { name: it.name, sku: it.sku || "", quantity: it.quantity, unit_price: itemCosts[key] ?? 0 };
    }));
    setPoOpen(true);
  };
  const updatePoItem = (i, field, val) => setPoItems((p) => p.map((it, idx) => (idx === i ? { ...it, [field]: val } : it)));
  const poCostTotal = poItems.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);

  const createPO = async () => {
    const lines = poItems.map((it) => ({ name: it.name, sku: it.sku, quantity: Number(it.quantity) || 1, unit_price: Number(it.unit_price) || 0, supplier_id: poSplit ? it.supplier_id : poSupplier }));
    if (lines.some((l) => !l.supplier_id)) return toast.error(poSplit ? "Choose a supplier for every item" : "Select a supplier");
    const groups = {};
    lines.forEach((l) => { (groups[l.supplier_id] = groups[l.supplier_id] || []).push(l); });
    try {
      for (const [sid, its] of Object.entries(groups)) {
        await api.post("/purchase-orders", {
          supplier_id: sid,
          order_id: id,
          items: its.map(({ supplier_id, ...rest }) => rest),
          notes: `Auto-generated for ${order.order_number}`,
        });
      }
      const n = Object.keys(groups).length;
      toast.success(n > 1 ? `${n} purchase orders created` : "Purchase order created");
      setPoOpen(false);
      setPoSupplier("");
      setPoSplit(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const createShipment = async () => {
    try {
      const payload = { order_id: id, ...ship };
      if (order.supply_type === "bulk") {
        payload.line_items = (order.items || []).map((it, i) => ({ name: it.name, sku: it.sku || "", quantity: Number(shipLines[i]) || 0 }));
        if (payload.line_items.every((l) => l.quantity <= 0)) return toast.error("Enter a quantity for at least one product to ship");
      }
      const { data } = await api.post("/shipments", payload);
      toast.success(`Shipment ${data.shipment_number} created`);
      setShipOpen(false);
      navigate(`/shipments/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const resyncShipped = async () => {
    try {
      await api.post(`/orders/${id}/resync-shipped`);
      toast.success("Shipped quantities recalculated from existing shipments");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openShipDialog = () => {
    setShip((s) => ({ ...s, carrier: order.preferred_carrier || s.carrier }));
    if (order.supply_type === "bulk") {
      setShipLines((order.items || []).map((it) => Math.max(0, (Number(it.quantity) || 0) - (Number(it.shipped) || 0))));
    }
    setShipOpen(true);
  };

  const [tpl, setTpl] = useState({ location: "", carrier_name: "", tracking_number: "", note: "" });
  const sendToDispatch = async () => {
    if (dispMode === "3pl") {
      const shp = (order?.shipments || []).find((x) => !["cancelled"].includes(x.status));
      if (!shp) return toast.error("No shipment exists for this order yet — create the shipment first");
      if (!tpl.location.trim()) return toast.error("Enter the 3PL dispatch location");
      try {
        await api.post(`/shipments/${shp.id}/dispatch-3pl`, tpl);
        toast.success("Marked dispatched by 3PL — Dubai dispatch team bypassed");
        setDispatchOpen(false); setTpl({ location: "", carrier_name: "", tracking_number: "", note: "" });
        load();
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
      return;
    }
    if (dispMode === "courier" && !courierName.trim()) return toast.error("Enter the courier name");
    if (dispMode === "air_cargo" && !airlineName.trim()) return toast.error("Enter the airline name");
    try {
      await api.post(`/orders/${id}/send-to-dispatch`, { mode: dispMode, courier_name: courierName, airline_name: airlineName });
      toast.success("Order sent to Dispatch");
      setDispatchOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const createInvoice = async () => {    try {
      const { data } = await api.post("/invoices", { order_id: id, tax_rate: Number(taxRate) || 0, currency: invCur, bank_account_id: invBank || null, exchange_rate: invCur !== "USD" && Number(invRate) > 0 ? Number(invRate) : null });
      toast.success(`Invoice ${data.invoice_number} generated`);
      setInvOpen(false);
      navigate(`/invoices/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteDoc = async (kind, doc) => {
    const label = kind === "invoice" ? `invoice ${doc.invoice_number}` : `purchase order ${doc.po_number || doc.number}`;
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      await api.delete(kind === "invoice" ? `/invoices/${doc.id}` : `/purchase-orders/${doc.id}`);
      toast.success(`Deleted ${label}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const openEditOrder = () => {
    setEf({
      items: order.items.map((it) => ({ name: it.name, sku: it.sku || "", quantity: it.quantity, unit_price: it.unit_price })),
      notes: order.notes || "",
      required_by: order.required_by || "",
      preferred_carrier: order.preferred_carrier || "",
      patient_name: order.patient_name || "",
      destination_country: order.destination_country || "",
      postal_code: order.postal_code || "",
      address: order.address || "",
      city: order.city || "",
      state: order.state || "",
      cpf: order.cpf || "",
      broker_details: order.broker_details || "",
      client_reference: order.client_reference || "",
    });
    setEditOpen(true);
  };
  const efItem = (i, f, v) => setEf((p) => ({ ...p, items: p.items.map((it, idx) => (idx === i ? { ...it, [f]: v } : it)) }));
  const efAddItem = () => setEf((p) => ({ ...p, items: [...p.items, { name: "", sku: "", quantity: 1, unit_price: 0 }] }));
  const efRemoveItem = (i) => setEf((p) => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));
  const efField = (f, v) => setEf((p) => ({ ...p, [f]: v }));
  const efCep = async (v) => {
    efField("postal_code", v);
    const d = v.replace(/\D/g, "");
    if (!["brazil", "brasil", "br"].includes((ef.destination_country || "").trim().toLowerCase()) || d.length !== 8) return;
    try {
      const j = await (await fetch(`https://viacep.com.br/ws/${d}/json/`)).json();
      if (!j.erro) setEf((p) => ({ ...p, postal_code: `${d.slice(0, 5)}-${d.slice(5)}`, city: j.localidade || p.city, state: j.uf || p.state }));
    } catch { /* offline lookup — leave fields as typed */ }
  };
  const saveEditOrder = async () => {
    const items = ef.items.filter((it) => it.name.trim()).map((it) => ({ name: it.name, sku: it.sku, quantity: Number(it.quantity) || 0, unit_price: Number(it.unit_price) || 0 }));
    if (items.length === 0) return toast.error("Add at least one line item");
    setEditBusy(true);
    try {
      await api.patch(`/orders/${id}/details`, {
        items, notes: ef.notes, required_by: ef.required_by || null,
        preferred_carrier: ef.preferred_carrier || null,
        patient_name: ef.patient_name, destination_country: ef.destination_country,
        postal_code: ef.postal_code, address: ef.address, city: ef.city, state: ef.state, cpf: ef.cpf, broker_details: ef.broker_details,
        client_reference: ef.client_reference,
      });
      toast.success("Order updated");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
    finally { setEditBusy(false); }
  };

  const startEditItems = () => setEditItems(order.items.map((it) => ({ ...it })));
  const changeItem = (i, field, val) => setEditItems((p) => p.map((it, idx) => (idx === i ? { ...it, [field]: val } : it)));
  const saveItems = async () => {
    setItemsBusy(true);
    try {
      const payload = editItems.map((it) => ({
        name: it.name, sku: it.sku || "",
        quantity: Number(it.quantity) || 0, unit_price: Number(it.unit_price) || 0,
      }));
      await api.patch(`/orders/${id}/items`, { items: payload });
      toast.success("Prices updated");
      setEditItems(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update prices");
    } finally {
      setItemsBusy(false);
    }
  };
  const editSubtotal = (editItems || []).reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);

  const pickDoc = (t) => {
    pendingDoc.current = t;
    docRef.current?.click();
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    const t = pendingDoc.current;
    if (!file || !t) return;
    setDocBusy(t.key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", t.label);
      fd.append("doc_type", t.key);
      await api.post(`/orders/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${t.label} uploaded`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setDocBusy(null);
      pendingDoc.current = null;
      if (docRef.current) docRef.current.value = "";
    }
  };

  const removeDoc = async (d) => {
    if (!window.confirm("Remove this file? You can upload a new one after.")) return;
    try { await api.delete(`/orders/${id}/documents/${d.id}`); toast.success("File removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove"); }
  };

  if (!order) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;

  const isBulk = order.supply_type === "bulk";
  const bulkTotals = (order.items || []).reduce((a, it) => { a.ordered += Number(it.quantity) || 0; a.shipped += Number(it.shipped) || 0; return a; }, { ordered: 0, shipped: 0 });
  const pctShipped = bulkTotals.ordered ? Math.round((bulkTotals.shipped / bulkTotals.ordered) * 100) : 0;

  return (
    <div className="space-y-8">
      <button onClick={() => navigate("/orders")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-orders">
        <ArrowLeft size={16} /> Orders
      </button>

      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-5">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">{order.client_name}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight leading-none mt-1">{order.order_number}</h1>
          {isBulk && <span className="inline-flex items-center gap-1.5 mt-2 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-2.5 py-0.5 text-[11px] uppercase tracking-wide font-semibold" data-testid="order-supply-badge"><Package size={13} weight="fill" /> Bulk supply</span>}
          {order.consolidated_invoice_id && <Link to={`/consolidated-invoices/${order.consolidated_invoice_id}`} className="inline-flex items-center gap-1.5 mt-2 ml-2 rounded-full bg-[#F5C64D]/20 text-[#7A5A00] border border-[#F5C64D] px-2.5 py-0.5 text-[11px] uppercase tracking-wide font-semibold hover:bg-[#F5C64D]/40" data-testid="order-consolidated-badge">In consolidated invoice {order.consolidated_invoice_number}</Link>}
          <p className="text-sm text-muted-foreground mt-2">Placed {fmtDate(order.created_at)} · {money(order.total)}{order.preferred_carrier ? ` · Preferred carrier: ${carrierLabel(order.preferred_carrier)}` : ""}</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusBadge status={order.status} />
          {order.required_by && <p className="text-xs text-muted-foreground">Required by {fmtDate(order.required_by)}</p>}
          {!isTeam && order.status !== "pending" && (
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => navigate(`/orders/new?reorder=${order.id}`)} data-testid="reorder-button">
              <ArrowCounterClockwise size={14} className="mr-1.5" /> Reorder
            </Button>
          )}
        </div>
      </div>

      {(order.patient_name || order.postal_code || order.address || order.destination_country || order.cpf || order.cep || order.broker_details || order.notes || order.client_reference) && (
        <div className="hp-card p-6" data-testid="order-destination-details">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-4">Destination details</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            {order.client_reference && <div data-testid="order-reference-display"><p className="text-xs uppercase tracking-wide text-muted-foreground">Reference no.</p><p className="font-medium mt-0.5">{order.client_reference}</p></div>}
            {order.patient_name && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Patient</p><p className="font-medium mt-0.5">{order.patient_name}</p></div>}
            {order.destination_country && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Country</p><p className="font-medium mt-0.5">{order.destination_country}</p></div>}
            {order.postal_code && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Postal code</p><p className="font-medium mt-0.5">{order.postal_code}</p></div>}
            {(order.city || order.state) && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">City / State</p><p className="font-medium mt-0.5" data-testid="order-city-state">{[order.city, order.state].filter(Boolean).join(", ")}</p></div>}
            {order.cep && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">CEP</p><p className="font-medium mt-0.5">{order.cep}</p></div>}
            {order.address && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Address</p><p className="font-medium mt-0.5">{order.address}</p></div>}
            {order.cpf && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">CPF</p><p className="font-medium mt-0.5">{order.cpf}</p></div>}
            {order.broker_details && <div className="sm:col-span-2 lg:col-span-3"><p className="text-xs uppercase tracking-wide text-muted-foreground">Broker</p><p className="font-medium mt-0.5 whitespace-pre-wrap">{order.broker_details}</p></div>}
            {order.notes && <div className="sm:col-span-2 lg:col-span-3" data-testid="order-notes-display"><p className="text-xs uppercase tracking-wide text-muted-foreground">Notes</p><p className="font-medium mt-0.5 whitespace-pre-wrap">{order.notes}</p></div>}
          </div>
        </div>
      )}

      {isTeam && (
        <div className="hp-card p-6 space-y-4">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Team Actions</p>
          {order.preferred_carrier && (
            <div className="flex items-center gap-2 text-sm rounded-lg bg-accent/10 border border-accent/30 px-3 py-2 w-fit" data-testid="order-preferred-carrier">
              <Truck size={16} className="text-primary" />
              <span className="text-muted-foreground">Client's chosen shipping method:</span>
              <CarrierBadge carrier={order.preferred_carrier} size="md" className="font-semibold" />
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            {isAdmin && (
              <Button variant="outline" className="rounded-sm border-primary/40 text-primary hover:bg-primary/5" onClick={openEditOrder} data-testid="edit-order-details">
                <PencilSimple size={16} className="mr-2" /> Edit Order
              </Button>
            )}
            {order.status === "pending" && (
              <>
                <Button className="rounded-lg bg-green-600 hover:bg-green-700" onClick={() => patchStatus({ status: "approved", procurement_status: "sourcing", shipping_charge: Number(shippingCharge) || 0, ...handlingPayload() })} data-testid="approve-order">
                  <CheckCircle size={16} className="mr-2" /> Approve
                </Button>
                <Button variant="outline" className="rounded-lg text-destructive border-destructive/40 hover:bg-destructive/5" onClick={() => patchStatus({ status: "rejected" })} data-testid="reject-order">
                  <XCircle size={16} className="mr-2" /> Reject
                </Button>
              </>
            )}
            <Button variant="outline" className="rounded-sm" onClick={openPoDialog} data-testid="create-po-from-order">
              <ClipboardText size={16} className="mr-2" /> Create PO
            </Button>
            <Button variant="outline" className="rounded-sm" onClick={openShipDialog} data-testid="create-shipment-from-order">
              <Truck size={16} className="mr-2" /> Create Shipment
            </Button>
            <Button variant="outline" className="rounded-sm border-[#4D148C]/40 text-[#4D148C] hover:bg-[#4D148C]/5" onClick={() => setFedexOpen(true)} data-testid="create-fedex-awb">
              <AirplaneTilt size={16} className="mr-2" /> Create FedEx AWB
            </Button>
            <Button variant="outline" className="rounded-sm" onClick={() => setInvOpen(true)} data-testid="create-invoice-from-order">
              <Receipt size={16} className="mr-2" /> Generate Invoice
            </Button>
            <AiEmailDraft orderId={order.id} onSent={load} size="default" />
            {!order.dispatch_id && order.status === "approved" && !isBulk && order.preferred_carrier !== "warehouse_pickup" && (
              <Button variant="outline" className="rounded-sm" onClick={() => setDispatchOpen(true)} data-testid="send-to-dispatch">
                <PaperPlaneTilt size={16} className="mr-2" /> Send to Dispatch
              </Button>
            )}
            {order.preferred_carrier === "warehouse_pickup" && !order.collection && order.status === "approved" && (
              <Button variant="outline" className="rounded-sm border-[#245FAF]/40 text-[#245FAF]" onClick={() => setCollectOpen(true)} data-testid="mark-collected">
                <Warehouse size={16} className="mr-2" /> Mark as Collected
              </Button>
            )}
            {order.collection && (
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-700 px-2" title={`Recorded by ${order.collection.recorded_by}`} data-testid="collected-chip">
                <Warehouse size={16} /> Collected by {order.collection.collected_by} · {fmtDateTime(order.collection.collected_at)}
              </span>
            )}
            {isBulk && order.status === "approved" && (
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground px-2" data-testid="bulk-dispatch-hint">
                <PaperPlaneTilt size={14} /> Send each shipment to dispatch from its shipment page
              </span>
            )}
            {isBulk && (
              <Button variant="ghost" size="sm" className="rounded-sm text-muted-foreground" title="Recalculate shipped quantities from the existing shipments" onClick={resyncShipped} data-testid="resync-shipped">
                <ArrowsClockwise size={14} className="mr-1.5" /> Recalculate shipped
              </Button>
            )}
            {order.dispatch_id && !isBulk && (
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-700 px-2" data-testid="dispatch-sent-chip">
                <PaperPlaneTilt size={16} /> {order.dispatch_status === "dispatched" ? "Dispatched" : "Sent to dispatch"}
              </span>
            )}
          </div>
          <div className="flex items-end gap-3 pt-2">
            <div className="w-56">
              <Label className="text-xs uppercase tracking-wide">Shipping charges ($)</Label>
              <Input type="number" min="0" step="0.01" className="mt-1.5" value={shippingCharge} onChange={(e) => setShippingCharge(e.target.value)} data-testid="shipping-charge-input" />
            </div>
            <Button variant="outline" onClick={() => patchStatus({ shipping_charge: Number(shippingCharge) || 0, ...handlingPayload() })} data-testid="apply-shipping-charge">Apply to total</Button>
            <p className="text-xs text-muted-foreground pb-2">Added to the client's final total &amp; invoice{order.status === "pending" ? " on approval" : ""}.</p>
          </div>

          <div className="pt-2 space-y-3" data-testid="handling-section">
            <Label className="text-xs uppercase tracking-wide">Handling</Label>
            <div className="inline-flex rounded-md border border-border bg-muted/40 p-1" data-testid="handling-tabs">
              <button
                type="button"
                onClick={() => setHandlingType("normal")}
                data-testid="handling-normal-tab"
                className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${handlingType === "normal" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              >
                Normal handling
              </button>
              <button
                type="button"
                onClick={() => setHandlingType("cool_box")}
                data-testid="handling-coolbox-tab"
                className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${handlingType === "cool_box" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              >
                Cool box
              </button>
            </div>
            {handlingType === "cool_box" && (
              <div className="grid sm:grid-cols-3 gap-4 items-end" data-testid="coolbox-fields">
                <div>
                  <Label className="text-xs uppercase tracking-wide">Cool box quantity</Label>
                  <Input type="number" min="0" step="1" className="mt-1.5 rounded-sm" value={coolBoxQty} onChange={(e) => setCoolBoxQty(e.target.value)} data-testid="coolbox-qty" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide">Price per cool box ($)</Label>
                  <Input type="number" min="0" step="0.01" className="mt-1.5 rounded-sm" value={coolBoxPrice} onChange={(e) => setCoolBoxPrice(e.target.value)} data-testid="coolbox-price" />
                </div>
                <div className="flex items-center justify-between rounded-sm bg-muted/40 px-3 py-2 h-10">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Total</span>
                  <span className="font-heading font-bold" data-testid="coolbox-total">{money((Number(coolBoxQty) || 0) * (Number(coolBoxPrice) || 0))}</span>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">Choose the handling method{order.status === "pending" ? " before approving" : ""}. Cool box cost is added to the client's final total. Use “Apply to total” or Approve to save.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 pt-2">
            <div>
              <Label className="text-xs uppercase tracking-wide">Procurement status</Label>
              <Select value={order.procurement_status} onValueChange={(v) => patchStatus({ procurement_status: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="proc-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PROC_STEPS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Shipping status</Label>
              <Select value={order.shipping_status} onValueChange={(v) => patchStatus({ shipping_status: v, status: v === "delivered" ? "delivered" : v === "not_shipped" ? order.status : "shipped" })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="ship-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SHIP_STEPS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-8 min-w-0">
        <div className="lg:col-span-2 space-y-8 min-w-0">
          {isBulk && (
            <div className="hp-card overflow-hidden" data-testid="bulk-fulfillment-panel">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3">
                <h3 className="font-heading font-bold flex items-center gap-2"><Package size={18} className="text-primary" /> Bulk fulfillment</h3>
                {order.fully_shipped ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 text-green-700 border border-green-200 px-2.5 py-0.5 text-[11px] uppercase tracking-wide font-semibold" data-testid="bulk-fully-shipped">
                    <CheckCircle size={13} weight="fill" /> Fully shipped
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-0.5 text-[11px] uppercase tracking-wide font-semibold" data-testid="bulk-in-progress">
                    {pctShipped}% shipped
                  </span>
                )}
              </div>
              <div className="px-4 pt-3">
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-primary transition-all" style={{ width: `${pctShipped}%` }} data-testid="bulk-progress-bar" />
                </div>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-sm mt-2">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2 font-semibold">Product</th>
                    <th className="px-4 py-2 font-semibold text-right">Ordered</th>
                    <th className="px-4 py-2 font-semibold text-right">Shipped</th>
                    <th className="px-4 py-2 font-semibold text-right">Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.items || []).map((it, i) => {
                    const ordered = Number(it.quantity) || 0;
                    const shipped = Number(it.shipped) || 0;
                    const remaining = Math.max(0, ordered - shipped);
                    return (
                      <tr key={i} className="border-t border-border" data-testid={`bulk-row-${i}`}>
                        <td className="px-4 py-2 font-medium">{it.name}</td>
                        <td className="px-4 py-2 text-right">{ordered}</td>
                        <td className="px-4 py-2 text-right" data-testid={`bulk-shipped-${i}`}>{shipped}</td>
                        <td className={`px-4 py-2 text-right font-semibold ${remaining === 0 ? "text-green-600" : "text-amber-600"}`} data-testid={`bulk-remaining-${i}`}>{remaining}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          )}
          <div className="hp-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <h3 className="font-heading font-bold">Line Items</h3>
              {isTeam && order.status === "pending" && !editItems && (
                <Button variant="outline" size="sm" className="rounded-sm" onClick={startEditItems} data-testid="edit-order-prices">
                  Adjust prices
                </Button>
              )}
              {editItems && (
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => setEditItems(null)} data-testid="cancel-edit-prices">Cancel</Button>
                  <Button size="sm" className="rounded-sm" onClick={saveItems} disabled={itemsBusy} data-testid="save-order-prices">{itemsBusy ? "Saving…" : "Save prices"}</Button>
                </div>
              )}
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider font-semibold text-muted-foreground bg-muted/40">
                  <th className="px-4 py-2 font-semibold">Product</th>
                  <th className="px-4 py-2 font-semibold">SKU</th>
                  <th className="px-4 py-2 font-semibold text-right">Qty</th>
                  <th className="px-4 py-2 font-semibold text-right">Unit</th>
                  <th className="px-4 py-2 font-semibold text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {editItems ? (
                  editItems.map((it, i) => (
                    <tr key={i} className={i % 2 ? "bg-muted/30" : ""}>
                      <td className="px-4 py-2 font-medium">{it.name}</td>
                      <td className="px-4 py-2 text-muted-foreground">{it.sku || "—"}</td>
                      <td className="px-2 py-2 text-right">
                        <Input type="number" min="0" className="h-8 w-20 ml-auto rounded-sm text-right" value={it.quantity} onChange={(e) => changeItem(i, "quantity", e.target.value)} data-testid={`edit-item-qty-${i}`} />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Input type="number" min="0" step="0.01" className="h-8 w-28 ml-auto rounded-sm text-right" value={it.unit_price} onChange={(e) => changeItem(i, "unit_price", e.target.value)} data-testid={`edit-item-price-${i}`} />
                      </td>
                      <td className="px-4 py-2 text-right">{money((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}</td>
                    </tr>
                  ))
                ) : (
                  (Array.isArray(order.items) ? order.items : []).map((it, i) => (
                    <tr key={i} className={i % 2 ? "bg-muted/30" : ""}>
                      <td className="px-4 py-2 font-medium">{it.name}</td>
                      <td className="px-4 py-2 text-muted-foreground">{it.sku || "—"}</td>
                      <td className="px-4 py-2 text-right">{it.quantity}</td>
                      <td className="px-4 py-2 text-right">{money(it.unit_price)}</td>
                      <td className="px-4 py-2 text-right">{money(it.quantity * it.unit_price)}</td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="border-t border-border">
                  <td colSpan={4} className="px-4 py-2 text-right text-xs uppercase tracking-wide font-semibold text-muted-foreground">Subtotal</td>
                  <td className="px-4 py-2 text-right" data-testid="order-subtotal">{money(editItems ? editSubtotal : (order.items_total ?? order.total))}</td>
                </tr>
                <tr>
                  <td colSpan={4} className="px-4 py-2 text-right text-xs uppercase tracking-wide font-semibold text-muted-foreground">Shipping</td>
                  <td className="px-4 py-2 text-right" data-testid="order-shipping">{money(order.shipping_charge || 0)}</td>
                </tr>
                {order.handling_type === "cool_box" && (order.handling_total || 0) > 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-2 text-right text-xs uppercase tracking-wide font-semibold text-muted-foreground" data-testid="order-handling-label">Cool box handling ({order.cool_box_qty} × {money(order.cool_box_price)})</td>
                    <td className="px-4 py-2 text-right" data-testid="order-handling">{money(order.handling_total)}</td>
                  </tr>
                )}
                <tr className="border-t border-border">
                  <td colSpan={4} className="px-4 py-3 text-right text-xs uppercase tracking-wide font-semibold text-muted-foreground">Total</td>
                  <td className="px-4 py-3 text-right font-heading font-extrabold" data-testid="order-total">{money(order.total)}</td>
                </tr>
              </tfoot>
            </table>
            </div>
          </div>

          {/* Order Documents */}
          <div className="hp-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <h3 className="font-heading font-bold flex items-center gap-2"><FileText size={18} className="text-primary" /> Documents</h3>
            </div>
            <input ref={docRef} type="file" className="hidden" onChange={uploadDoc} data-testid="order-doc-input" />
            <div className="divide-y divide-border">
              {DOC_TYPES.map((t) => {
                const files = (order.documents || []).filter((d) => (d.doc_type || "other") === t.key);
                return (
                  <div key={t.key} className="px-4 py-3" data-testid={`order-doc-group-${t.key}`}>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium">{t.label}</p>
                      <Button variant="outline" size="sm" className="rounded-sm" onClick={() => pickDoc(t)} disabled={docBusy === t.key} data-testid={`upload-doc-${t.key}`}>
                        <UploadSimple size={14} className="mr-1" /> {docBusy === t.key ? "Uploading…" : (files.length ? "Add another" : "Upload")}
                      </Button>
                    </div>
                    {files.length === 0 ? (
                      <p className="text-xs text-muted-foreground mt-1">Not uploaded yet.</p>
                    ) : (
                      <div className="mt-2 space-y-1.5">
                        {files.map((d) => (
                          <div key={d.id} className="flex items-center justify-between gap-3 rounded-sm bg-muted/40 px-3 py-2" data-testid={`order-doc-${d.id}`}>
                            <div className="min-w-0">
                              <p className="text-sm truncate">{d.original_filename}</p>
                              <p className="text-xs text-muted-foreground">{d.uploaded_by} · {fmtDateTime(d.uploaded_at)}</p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => downloadStoredFile(d.storage_path, d.original_filename)} data-testid={`download-order-doc-${d.id}`}>
                                <DownloadSimple size={16} />
                              </Button>
                              <Button variant="ghost" size="sm" className="rounded-sm text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => removeDoc(d)} data-testid={`order-doc-remove-${d.id}`}>
                                <Trash size={16} />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {(order.documents || []).filter((d) => !DOC_TYPES.some((t) => t.key === (d.doc_type || "other"))).length > 0 && (
                <div className="px-4 py-3" data-testid="order-doc-group-other">
                  <p className="text-sm font-medium">Other</p>
                  <div className="mt-2 space-y-1.5">
                    {(order.documents || []).filter((d) => !DOC_TYPES.some((t) => t.key === (d.doc_type || "other"))).map((d) => (
                      <div key={d.id} className="flex items-center justify-between gap-3 rounded-sm bg-muted/40 px-3 py-2" data-testid={`order-doc-${d.id}`}>
                        <div className="min-w-0">
                          <p className="text-sm truncate">{d.label || d.original_filename}</p>
                          <p className="text-xs text-muted-foreground">{d.uploaded_by} · {fmtDateTime(d.uploaded_at)}</p>
                        </div>
                        <Button variant="ghost" size="sm" className="rounded-sm shrink-0" onClick={() => downloadStoredFile(d.storage_path, d.original_filename)} data-testid={`download-order-doc-${d.id}`}>
                          <DownloadSimple size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {order.order_progress?.items?.length > 0 && (
            <div className="hp-card p-5" data-testid="order-b2b-progress">
              <h3 className="font-heading font-bold flex items-center gap-2 mb-3"><Package size={18} className="text-primary" /> Preparation progress <span className="text-[10px] uppercase tracking-wider font-semibold rounded-sm bg-muted px-1.5 py-0.5 text-muted-foreground">No PTN · B2B</span></h3>
              <OrderProgress p={order.order_progress} linkTo={(x) => `/shipments/${x.id}`} />
            </div>
          )}

          {order.shipments?.length > 0 && (
            <div className="hp-card overflow-hidden">
              <div className="px-4 py-3 border-b border-border"><h3 className="font-heading font-bold">Shipments</h3></div>
              {order.shipments.map((s) => (
                <div key={s.id} onClick={() => navigate(`/shipments/${s.id}`)} className="flex items-center justify-between px-4 py-3 border-b border-border last:border-0 cursor-pointer hover:bg-primary/5 transition-colors" data-testid={`order-shipment-${s.shipment_number}`}>
                  <div>
                    <p className="font-medium text-sm">{s.shipment_number}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><CarrierBadge carrier={s.carrier} /> · {s.photos?.length || 0} photos</p>
                  </div>
                  <StatusBadge status={s.status} />
                </div>
              ))}
            </div>
          )}

          {order.invoices?.length > 0 && (
            <div className="hp-card overflow-hidden">
              <div className="px-4 py-3 border-b border-border"><h3 className="font-heading font-bold">Invoices</h3></div>
              {order.invoices.map((inv) => (
                <div key={inv.id} onClick={() => navigate(`/invoices/${inv.id}`)} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border last:border-0 cursor-pointer hover:bg-primary/5 transition-colors" data-testid={`order-invoice-${inv.invoice_number}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{inv.invoice_number}</p>
                    <p className="text-xs text-muted-foreground">{money(inv.total)} · due {fmtDate(inv.due_date)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <StatusBadge status={inv.status} />
                    {isTeam && inv.status !== "cancelled" && <button type="button" onClick={(e) => { e.stopPropagation(); setFeeInv(inv); setFeeAmt(inv.transfer_fee || ""); setFeeNote(inv.transfer_fee_note || ""); }} className={`text-xs px-2 py-1 rounded-sm border border-border hover:bg-primary/10 ${inv.transfer_fee > 0 ? "text-primary font-medium" : "text-muted-foreground"}`} title="Add / edit warehouse location transfer fee" data-testid={`transfer-fee-${inv.invoice_number}`}>{inv.transfer_fee > 0 ? `Fee ${money(inv.transfer_fee)}` : "+ Transfer fee"}</button>}
                    {isTeam && inv.status !== "paid" && <button type="button" onClick={(e) => { e.stopPropagation(); deleteDoc("invoice", inv); }} className="text-muted-foreground hover:text-destructive p-1" title="Delete invoice" data-testid={`delete-order-invoice-${inv.invoice_number}`}><Trash size={15} /></button>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {isTeam && order.purchase_orders?.length > 0 && (
            <div className="hp-card overflow-hidden">
              <div className="px-4 py-3 border-b border-border"><h3 className="font-heading font-bold">Purchase Orders</h3></div>
              {order.purchase_orders.map((po) => { const n = po.po_number || po.number; return (
                <div key={po.id} onClick={() => navigate(`/purchase-orders/${po.id}`)} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border last:border-0 cursor-pointer hover:bg-primary/5 transition-colors" data-testid={`order-po-${n}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{n}</p>
                    <p className="text-xs text-muted-foreground truncate">{po.supplier_name || po.supplier || "Supplier"}{po.total ? ` · ${money(po.total)}` : ""} · {fmtDate(po.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <StatusBadge status={po.status} />
                    <button type="button" onClick={(e) => { e.stopPropagation(); deleteDoc("po", po); }} className="text-muted-foreground hover:text-destructive p-1" title="Delete purchase order" data-testid={`delete-order-po-${n}`}><Trash size={15} /></button>
                  </div>
                </div>
              ); })}
            </div>
          )}
        </div>

        <div className="space-y-8">
          <div className="hp-card p-6">
            <h3 className="font-heading font-bold mb-4">Procurement</h3>
            <Stepper steps={PROC_STEPS} current={order.procurement_status} />
          </div>
          <div className="hp-card p-6">
            <h3 className="font-heading font-bold mb-4">Shipping</h3>
            {!isTeam && order.shipments?.length > 0 ? (
              <div className="space-y-6" data-testid="order-tracking-timelines">
                {order.shipments.map((s) => (
                  <div key={s.id}>
                    {order.shipments.length > 1 && <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2">{s.shipment_number} · {carrierLabel(s.carrier)}</p>}
                    <TrackingTimeline shipment={s} orderCreatedAt={order.created_at} compact />
                  </div>
                ))}
              </div>
            ) : <Stepper steps={SHIP_STEPS} current={order.shipping_status} />}
          </div>
          <div className="hp-card p-6">
            <h3 className="font-heading font-bold mb-4">Activity</h3>
            <div className="space-y-3">
              {(order.history || []).slice().reverse().map((h, i) => (
                <div key={i} className="text-sm">
                  <p className="text-foreground">{h.event}</p>
                  <p className="text-xs text-muted-foreground">{h.by} · {fmtDateTime(h.ts)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* PO dialog */}
      <Dialog open={poOpen} onOpenChange={setPoOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Create Purchase Order</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-1 p-1 bg-muted/60 rounded-sm w-fit" data-testid="po-mode-toggle">
              <button type="button" onClick={() => setPoSplit(false)} className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${!poSplit ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="po-mode-single">One supplier</button>
              <button type="button" onClick={() => setPoSplit(true)} className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${poSplit ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="po-mode-split">Different suppliers per item</button>
            </div>
            {!poSplit && (
            <div>
              <Label className="text-xs uppercase tracking-wide">Supplier</Label>
              <Select value={poSupplier} onValueChange={setPoSupplier}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="po-supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
              {suppliers.length === 0 && <p className="text-xs text-muted-foreground mt-1">No suppliers yet. Add one under Suppliers.</p>}
            </div>
            )}

            <div className="border border-border rounded-md overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/50 text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                <div className={poSplit ? "col-span-4" : "col-span-6"}>Item</div>
                {poSplit && <div className="col-span-3">Supplier</div>}
                <div className="col-span-2 text-right">Qty</div>
                <div className={poSplit ? "col-span-3 text-right" : "col-span-4 text-right"}>Purchase price (cost)</div>
              </div>
              {poItems.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center" data-testid={`po-line-${i}`}>
                  <div className={`${poSplit ? "col-span-4" : "col-span-6"} text-sm truncate`}>{it.name}{it.sku ? ` · ${it.sku}` : ""}</div>
                  {poSplit && (
                    <Select value={it.supplier_id || ""} onValueChange={(v) => updatePoItem(i, "supplier_id", v)}>
                      <SelectTrigger className="col-span-3 rounded-sm h-9" data-testid={`po-line-supplier-${i}`}><SelectValue placeholder="Supplier" /></SelectTrigger>
                      <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  <div className="col-span-2 text-right text-sm text-muted-foreground">{it.quantity}</div>
                  <Input type="number" min="0" step="0.01" className={`${poSplit ? "col-span-3" : "col-span-4"} rounded-sm h-9 text-right`} value={it.unit_price} onChange={(e) => updatePoItem(i, "unit_price", e.target.value)} data-testid={`po-line-cost-${i}`} />
                </div>
              ))}
              {poItems.length === 0 && <p className="px-3 py-3 text-xs text-muted-foreground">This order has no items.</p>}
            </div>
            <p className="text-xs text-muted-foreground">{poSplit ? "One purchase order will be created per supplier, grouping the items you assign to each. " : ""}Enter the supplier cost per item — this is separate from the client selling price and drives the expected profit. Costs are remembered and prefilled next time.</p>
            <div className="flex justify-between items-center border-t border-border pt-3">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">{poSplit ? `Total (cost) · ${new Set(poItems.map((it) => it.supplier_id).filter(Boolean)).size} supplier(s)` : "PO Total (cost)"}</span>
              <span className="font-heading text-xl font-extrabold" data-testid="po-dialog-total">{money(poCostTotal)}</span>
            </div>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={createPO} data-testid="confirm-create-po">{poSplit ? "Create POs" : "Create PO"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shipment dialog */}
      <FedexAwbDialog open={fedexOpen} onOpenChange={setFedexOpen} orderId={id} onCreated={(d) => { load(); navigate(`/shipments/${d.shipment_id}`); }} />
      <Dialog open={shipOpen} onOpenChange={setShipOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Shipment</DialogTitle></DialogHeader>
          {isBulk && (
            <div className="border border-border rounded-md overflow-hidden" data-testid="bulk-ship-picker">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/50 text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                <div className="col-span-6">Product</div>
                <div className="col-span-3 text-right">Remaining</div>
                <div className="col-span-3 text-right">Ship now</div>
              </div>
              {(order.items || []).map((it, i) => {
                const remaining = Math.max(0, (Number(it.quantity) || 0) - (Number(it.shipped) || 0));
                return (
                  <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center" data-testid={`bulk-ship-line-${i}`}>
                    <div className="col-span-6 text-sm truncate">{it.name}</div>
                    <div className="col-span-3 text-right text-sm text-muted-foreground">{remaining}</div>
                    <Input type="number" min="0" max={remaining} className="col-span-3 rounded-sm h-9 text-right" value={shipLines[i] ?? 0} onChange={(e) => setShipLines((p) => p.map((v, idx) => (idx === i ? e.target.value : v)))} data-testid={`bulk-ship-qty-${i}`} />
                  </div>
                );
              })}
              <p className="px-3 py-2 text-xs text-muted-foreground">You can't ship more than the remaining quantity of any product.</p>
              <div className="px-3 pb-3">
                <Label className="text-xs uppercase tracking-wide">Patient (PTN) name for this shipment</Label>
                <Input className="mt-1.5 rounded-sm" placeholder="Optional — e.g. John Doe" value={ship.patient_name} onChange={(e) => setShip({ ...ship, patient_name: e.target.value })} data-testid="bulk-ship-ptn" />
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wide">Carrier</Label>
              <Select value={ship.carrier} onValueChange={(v) => setShip({ ...ship, carrier: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="ship-carrier"><SelectValue placeholder="Select carrier" /></SelectTrigger>
                <SelectContent>{CARRIER_OPTIONS.map((c) => <SelectItem key={c.code} value={c.code}><CarrierBadge carrier={c.code} /></SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-wide">AWB / Tracking #</Label><Input className="mt-1.5 rounded-sm" value={ship.tracking_number} onChange={(e) => setShip({ ...ship, tracking_number: e.target.value })} data-testid="ship-tracking" /></div>
            <div><Label className="text-xs uppercase tracking-wide">ETA</Label><Input type="date" className="mt-1.5 rounded-sm" value={ship.eta} onChange={(e) => setShip({ ...ship, eta: e.target.value })} data-testid="ship-eta" /></div>
            <div><Label className="text-xs uppercase tracking-wide">Origin</Label><Input className="mt-1.5 rounded-sm" value={ship.origin} onChange={(e) => setShip({ ...ship, origin: e.target.value })} data-testid="ship-origin" /></div>
            <div><Label className="text-xs uppercase tracking-wide">Destination</Label><Input className="mt-1.5 rounded-sm" value={ship.destination} onChange={(e) => setShip({ ...ship, destination: e.target.value })} data-testid="ship-destination" /></div>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={createShipment} data-testid="confirm-create-shipment">Create Shipment</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer fee dialog */}
      <Dialog open={!!feeInv} onOpenChange={(o) => { if (!o) setFeeInv(null); }}>
        <DialogContent data-testid="transfer-fee-dialog">
          <DialogHeader><DialogTitle>Warehouse transfer fee — {feeInv?.invoice_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Fee for moving stock between warehouse locations, added on top of the invoice total ({feeInv?.currency || "USD"}). The invoice page, PDF and client view update immediately. Set to 0 to remove.</p>
            <div><Label className="text-xs uppercase tracking-wide">Fee amount ({feeInv?.currency || "USD"})</Label><Input type="number" min="0" step="0.01" className="mt-1.5 rounded-sm" value={feeAmt} onChange={(e) => setFeeAmt(e.target.value)} data-testid="transfer-fee-amount" /></div>
            <div><Label className="text-xs uppercase tracking-wide">Note (optional)</Label><Input className="mt-1.5 rounded-sm" placeholder="e.g. Transfer from Dubai to Cairo warehouse" value={feeNote} onChange={(e) => setFeeNote(e.target.value)} data-testid="transfer-fee-note" /></div>
          </div>
          <DialogFooter><Button className="rounded-sm" disabled={feeBusy} onClick={saveTransferFee} data-testid="transfer-fee-save">{feeBusy ? "Saving…" : "Save & update invoice"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice dialog */}
      <Dialog open={invOpen} onOpenChange={setInvOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Generate Invoice</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Subtotal {money(order.total)}</p>
            <Label className="text-xs uppercase tracking-wide">Tax rate (%)</Label>
            <Input type="number" min="0" step="0.1" className="rounded-sm" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} data-testid="invoice-tax-rate" />
            <BankAccountPicker currency={invCur} setCurrency={setInvCur} accountId={invBank} setAccountId={setInvBank} accounts={bankAccounts} rate={invRate} setRate={setInvRate} baseTotal={order.total} />
            <p className="text-xs text-muted-foreground">The chosen bank account is printed on the invoice; you can change it later from the invoice page.</p>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={createInvoice} data-testid="confirm-create-invoice">Generate</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {isTeam && (
        <div className="hp-card p-5 space-y-3" data-testid="order-transit-section">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div><h3 className="font-heading font-bold flex items-center gap-2"><ArrowsClockwise size={18} className="text-primary" /> Transit shipments <span className="text-[10px] uppercase tracking-wider font-semibold rounded-sm bg-muted px-1.5 py-0.5 text-muted-foreground">Team only</span></h3>
              <p className="text-sm text-muted-foreground mt-1">Warehouse → 3PL transfers for this order, tracked by AWB.</p></div>
            <Button variant="outline" className="rounded-sm" onClick={() => navigate(`/transit-shipments?order_id=${order.id}&q=${order.order_number}`)} data-testid="order-add-transit"><Plus size={16} className="mr-2" /> New transit shipment</Button>
          </div>
          {transits.length > 0 && (
            <ul className="divide-y divide-border border border-border rounded-md" data-testid="order-transit-list">{transits.map((t) => (
              <li key={t.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm" data-testid={`order-transit-${t.id}`}>
                <Link to={`/transit-shipments?q=${t.number}`} className="font-semibold text-primary hover:underline">{t.number}</Link><StatusBadge status={t.status} />
                <span className="text-muted-foreground">{t.from_location} → {t.to_location}</span>
                <span className="font-mono text-xs">AWB {t.awb_number}</span>
                {t.tracking_url && <a href={t.tracking_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">Track ↗</a>}
                <span className="ml-auto flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{t.shipped_on ? fmtDate(t.shipped_on) : ""}</span>
                  <AiEmailDraft transitId={t.id} defaultPurpose="reexport" label="Re-export instructions" onSent={loadTransits} />
                </span>
                <TransitAttachments transit={t} onChanged={loadTransits} />
              </li>
            ))}</ul>
          )}
        </div>
      )}

      {/* Collect at Warehouse dialog */}
      <Dialog open={collectOpen} onOpenChange={setCollectOpen}>
        <DialogContent data-testid="collect-dialog">
          <DialogHeader><DialogTitle>Mark as Collected</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs uppercase tracking-wide">Collected by</Label><Input className="mt-1.5 rounded-sm" value={collect.collected_by} onChange={(e) => setCollect({ ...collect, collected_by: e.target.value })} placeholder="Name of the person who picked up" data-testid="collect-by" /></div>
            <div><Label className="text-xs uppercase tracking-wide">ID / authorisation ref (optional)</Label><Input className="mt-1.5 rounded-sm" value={collect.id_reference} onChange={(e) => setCollect({ ...collect, id_reference: e.target.value })} placeholder="e.g. Emirates ID 784-…" data-testid="collect-id" /></div>
            <div><Label className="text-xs uppercase tracking-wide">Note (optional)</Label><Input className="mt-1.5 rounded-sm" value={collect.note} onChange={(e) => setCollect({ ...collect, note: e.target.value })} data-testid="collect-note" /></div>
            <p className="text-xs text-muted-foreground">Records the pickup now ({fmtDateTime(new Date().toISOString())}), closes the order as shipped/collected and notifies the client. No dispatch job is created.</p>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={markCollected} data-testid="confirm-collected">Mark as Collected</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send to Dispatch dialog */}
      <Dialog open={dispatchOpen} onOpenChange={setDispatchOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send to Dispatch</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="inline-flex rounded-md border border-border bg-muted/40 p-1" data-testid="dispatch-mode-tabs">
              <button type="button" onClick={() => setDispMode("courier")} data-testid="dispatch-mode-courier" className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${dispMode === "courier" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>Courier</button>
              <button type="button" onClick={() => setDispMode("air_cargo")} data-testid="dispatch-mode-air" className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${dispMode === "air_cargo" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>Air Cargo</button>
              <button type="button" onClick={() => setDispMode("3pl")} data-testid="dispatch-mode-3pl" className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${dispMode === "3pl" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>3PL Dispatch</button>
            </div>
            {dispMode === "3pl" ? (
              <div className="space-y-3" data-testid="dispatch-3pl-form">
                <div><Label className="text-xs uppercase tracking-wide">Ships from (location / 3PL partner) *</Label><Input className="mt-1.5 rounded-sm" value={tpl.location} onChange={(e) => setTpl({ ...tpl, location: e.target.value })} placeholder="e.g. Partner warehouse, Panama City" data-testid="dispatch-3pl-location" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-wide">Carrier (optional)</Label><Input className="mt-1.5 rounded-sm" value={tpl.carrier_name} onChange={(e) => setTpl({ ...tpl, carrier_name: e.target.value })} data-testid="dispatch-3pl-carrier" /></div>
                  <div><Label className="text-xs uppercase tracking-wide">Tracking / AWB (optional)</Label><Input className="mt-1.5 rounded-sm" value={tpl.tracking_number} onChange={(e) => setTpl({ ...tpl, tracking_number: e.target.value })} data-testid="dispatch-3pl-tracking" /></div>
                </div>
                <div><Label className="text-xs uppercase tracking-wide">Note (optional)</Label><Input className="mt-1.5 rounded-sm" value={tpl.note} onChange={(e) => setTpl({ ...tpl, note: e.target.value })} data-testid="dispatch-3pl-note" /></div>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">The shipment is marked <b>dispatched</b> immediately from the 3PL location — the Dubai dispatch team is bypassed and the client is notified.{!(order?.shipments || []).length && " A shipment must exist for this order first."}</p>
              </div>
            ) : dispMode === "courier" ? (
              <div><Label className="text-xs uppercase tracking-wide">Courier name</Label><Input className="mt-1.5 rounded-sm" value={courierName} onChange={(e) => setCourierName(e.target.value)} placeholder="e.g. Aramex, FedEx" data-testid="dispatch-courier-name" /></div>
            ) : (
              <div><Label className="text-xs uppercase tracking-wide">Airline name</Label><Input className="mt-1.5 rounded-sm" value={airlineName} onChange={(e) => setAirlineName(e.target.value)} placeholder="e.g. Emirates SkyCargo" data-testid="dispatch-airline-name" /></div>
            )}
            {dispMode !== "3pl" && <p className="text-xs text-muted-foreground">The dispatcher sees only PTN name, products & quantities{dispMode === "air_cargo" ? ", destination" : ""} and the order number — not the client or prices. Upload the box &amp; AWB documents for them on the dispatch page.</p>}
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={sendToDispatch} data-testid="confirm-send-to-dispatch">{dispMode === "3pl" ? "Mark dispatched by 3PL" : "Send to Dispatch"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="edit-order-dialog">
          <DialogHeader><DialogTitle>Edit Order {order.order_number}</DialogTitle></DialogHeader>
          {ef && (
            <div className="space-y-5">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase tracking-wide">Line items</Label>
                  <Button variant="outline" size="sm" className="rounded-sm h-7" onClick={efAddItem} data-testid="edit-add-item"><Plus size={12} className="mr-1" /> Add item</Button>
                </div>
                <div className="space-y-2">
                  {ef.items.map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`edit-order-item-${i}`}>
                      <Input className="col-span-5 rounded-sm h-9" placeholder="Product name" value={it.name} onChange={(e) => efItem(i, "name", e.target.value)} data-testid={`edit-order-item-name-${i}`} />
                      <Input className="col-span-2 rounded-sm h-9" placeholder="SKU" value={it.sku} onChange={(e) => efItem(i, "sku", e.target.value)} data-testid={`edit-order-item-sku-${i}`} />
                      <Input type="number" min="0" className="col-span-2 rounded-sm h-9 text-right" value={it.quantity} onChange={(e) => efItem(i, "quantity", e.target.value)} data-testid={`edit-order-item-qty-${i}`} />
                      <Input type="number" min="0" step="0.01" className="col-span-2 rounded-sm h-9 text-right" value={it.unit_price} onChange={(e) => efItem(i, "unit_price", e.target.value)} data-testid={`edit-order-item-price-${i}`} />
                      <button className="col-span-1 flex justify-center text-muted-foreground hover:text-destructive disabled:opacity-30" onClick={() => efRemoveItem(i)} disabled={ef.items.length === 1} data-testid={`edit-order-item-remove-${i}`}><Trash size={16} /></button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-xs uppercase tracking-wide">Patient / recipient</Label><Input className="mt-1.5 rounded-sm" value={ef.patient_name} onChange={(e) => efField("patient_name", e.target.value)} data-testid="edit-order-patient" /></div>
                <div><Label className="text-xs uppercase tracking-wide">Reference no.</Label><Input className="mt-1.5 rounded-sm" value={ef.client_reference} onChange={(e) => efField("client_reference", e.target.value)} data-testid="edit-order-reference" /></div>
                <div><Label className="text-xs uppercase tracking-wide">Destination country</Label><Input className="mt-1.5 rounded-sm" value={ef.destination_country} onChange={(e) => efField("destination_country", e.target.value)} data-testid="edit-order-destcountry" /></div>
                <div><Label className="text-xs uppercase tracking-wide">Postal code</Label><Input className="mt-1.5 rounded-sm" value={ef.postal_code} onChange={(e) => efCep(e.target.value)} placeholder="Brazil: CEP fills city & state" data-testid="edit-order-postal" /></div>
                <div><Label className="text-xs uppercase tracking-wide">City</Label><Input className="mt-1.5 rounded-sm" value={ef.city} onChange={(e) => efField("city", e.target.value)} data-testid="edit-order-city" /></div>
                <div><Label className="text-xs uppercase tracking-wide">State</Label><Input className="mt-1.5 rounded-sm uppercase" value={ef.state} onChange={(e) => efField("state", e.target.value.toUpperCase())} data-testid="edit-order-state" /></div>
                <div><Label className="text-xs uppercase tracking-wide">Address</Label><Input className="mt-1.5 rounded-sm" value={ef.address} onChange={(e) => efField("address", e.target.value)} data-testid="edit-order-address" /></div>
                <div><Label className="text-xs uppercase tracking-wide">CPF</Label><Input className="mt-1.5 rounded-sm" value={ef.cpf} onChange={(e) => efField("cpf", e.target.value)} data-testid="edit-order-cpf" /></div>
                <div>
                  <Label className="text-xs uppercase tracking-wide">Preferred carrier</Label>
                  <Select value={ef.preferred_carrier || "none"} onValueChange={(v) => efField("preferred_carrier", v === "none" ? "" : v)}>
                    <SelectTrigger className="mt-1.5 rounded-sm" data-testid="edit-order-carrier"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {CARRIER_OPTIONS.map((c) => <SelectItem key={c.code} value={c.code}><CarrierBadge carrier={c.code} /></SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs uppercase tracking-wide">Required by</Label><Input type="date" className="mt-1.5 rounded-sm" value={ef.required_by ? ef.required_by.slice(0, 10) : ""} onChange={(e) => efField("required_by", e.target.value)} data-testid="edit-order-requiredby" /></div>
              </div>
              <div><Label className="text-xs uppercase tracking-wide">Broker details</Label><Textarea rows={2} className="mt-1.5 rounded-sm" value={ef.broker_details} onChange={(e) => efField("broker_details", e.target.value)} data-testid="edit-order-broker" /></div>
              <div><Label className="text-xs uppercase tracking-wide">Notes</Label><Textarea rows={2} className="mt-1.5 rounded-sm" value={ef.notes} onChange={(e) => efField("notes", e.target.value)} data-testid="edit-order-notes" /></div>
            </div>
          )}
          <DialogFooter><Button className="rounded-sm" onClick={saveEditOrder} disabled={editBusy} data-testid="edit-order-save">{editBusy ? "Saving…" : "Save changes"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
